import AddToCartFormik from '@jetshop/core/components/Mutation/AddToCartFormik';
import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import t from '@jetshop/intl';
import { Price } from '@jetshop/ui/Price';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { Form } from 'formik';
import React, { useContext } from 'react';
import { styled } from 'linaria/react';
import addToCartMutation from '../../Cart/addToCart.gql';
import CartQuery from '../../Cart/CartQuery.gql';
import InputWithLabel from '../../Forms/Input';
import Button from '../../ui/Button';
import NotifyWhenBack from '../StockStatus/NotifyWhenBack';
import StockStatusIndicator from '../StockStatus/StockStatusIndicator';
import { useProductValidationMessage } from '../useProductValidationMessage';
import { VariantSelector } from '../VariantSelector';
import { theme } from '../../Theme';
import { useNotification } from '@jetshop/core/components/Notifications';
import ProductToastWrapper from './ProductToast';
import { ConfigurationSelector } from '../ConfigurationSelector';

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  ${theme.below.md} {
    align-items: center;
  }
`;

const QuantityAddToCartWrapper = styled('div')`
  display: flex;
  width: 100%;
  align-items: flex-end;
  .quantity-input-wrapper {
    display: none;
  }
`;

const BuyButton = styled(Button)`
  width: 100%;
  font-weight: 600;
  color: white;
  border-radius: 30px;
  background: ${theme.colors.primary};
  height: 50px;
  line-height: 50px;
  padding: 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 14px;
  &:hover {
    background: #4c4c4c;
  }

  &:focus {
    outline: none;
  }

  ${theme.below.md} {
    background: ${theme.colors.primary};
    color: white;
    padding: 0 10px;
    height: 50px;
    line-height: 50px;
  }
`;
const PriceWrapper = styled('div')`
  padding-top: 20px;
  border-top: 1px solid #dedede;
  padding-bottom: 20px;
  ${theme.below.md} {
    padding: 0;
    border: 0;
    .price {
      color: black;
    }
  }
`;

const Group = styled('div')`
  margin-bottom: 30px;
`;

function AddToCartForm({
  product,
  variant,
  getMissingOptions,
  variantHandler
}) {
  const configurationContext = useContext(ProductConfigurationContext);
  const price = useDynamicPrice(product, variant);
  const stockStatus = useStockStatus(variant || product);
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';
  const { variantValidation, missingOptions } = variantHandler;
  const { hasVariants } = product;
  const { validationMessage, enableValidation } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });
  const [trigger, dismiss] = useNotification();

  const onAddToCartInit = ({ mutationId, quantity, price }) => {
    trigger(
      <ProductToastWrapper
        selectedVariation={variant}
        product={product}
        quantity={quantity}
        price={price}
      />,
      {
        id: mutationId,
        type: 'add-to-cart'
      }
    );
  };
  const onAddToCartError = () => {
    return ({ selectedVariation, mutationId, quantity, price, error }) => {
      dismiss(mutationId);

      trigger(
        <ProductToastWrapper
          selectedVariation={variant}
          product={product}
          quantity={quantity}
          price={price}
          error={error}
        />,
        {
          type: 'add-to-cart'
        }
      );
    };
  };
  return (
    <AddToCartFormik
      onAddToCartInit={onAddToCartInit}
      onAddToCartError={onAddToCartError}
      cartQuery={CartQuery}
      cartMutation={addToCartMutation}
      product={product}
      variant={variant}
      getMissingOptions={getMissingOptions}
    >
      {() => {
        return (
          <>
            <PriceWrapper className="price-wrapper">
              <Price
                {...price}
                css={{
                  opacity:
                    configurationContext && configurationContext.loading
                      ? 0.3
                      : 1
                }}
              />
            </PriceWrapper>

            <Group>
              {product.hasVariants && (
                <VariantSelector
                  product={product}
                  variantHandler={variantHandler}
                  showValidation={!!validationMessage}
                />
              )}
              {product.hasConfigurations && (
                <ConfigurationSelector product={product} />
              )}

              {/* <IncludedInPackages packages={product.inPackages} /> */}

              <StyledForm>
                {product.customerComments &&
                  product.customerComments.map(comment => (
                    <InputWithLabel
                      wrapperClassName="customer-comment-input-wrapper"
                      className="customer-comment-input"
                      data-testid={`${comment.name}-input`}
                      id={`comments[${comment.name}]`}
                      name={`comments[${comment.name}]`}
                      label={comment.name}
                      key={comment.name}
                    />
                  ))}
                {!outOfStockNotify && (
                  <QuantityAddToCartWrapper>
                    <InputWithLabel
                      disableValidation
                      wrapperClassName="quantity-input-wrapper"
                      min="1"
                      type="number"
                      name="quantity"
                      label="Quantity"
                    />
                    <BuyButton
                      data-testid="add-to-cart"
                      type="submit"
                      disabled={!!validationMessage}
                      onClick={enableValidation}
                      style={{
                        opacity:
                          !!validationMessage || !product.stockStatus.buyable
                            ? 0.5
                            : 1,
                        cursor:
                          !!validationMessage || !product.stockStatus.buyable
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                    >
                      {validationMessage ? validationMessage : t('Add to cart')}
                    </BuyButton>
                  </QuantityAddToCartWrapper>
                )}
              </StyledForm>
              <StockStatusIndicator
                status={stockStatus.status}
                text={stockStatus.text}
              />
              {outOfStockNotify && (
                <NotifyWhenBack
                  text={stockStatus.text}
                  articleNumber={(variant || product).articleNumber}
                />
              )}
            </Group>
          </>
        );
      }}
    </AddToCartFormik>
  );
}

export default AddToCartForm;
